import React, { useState, MouseEvent, useEffect, useCallback } from 'react';
import { format as formatDate } from 'date-fns';
import { es } from 'date-fns/locale';
import { flip, offset, shift, useFloating } from '@floating-ui/react-dom';

import DateIcon from '../../icons/date-icon/date-icon';

import styles from './date-picker.module.scss';
import { DatePickerModal } from './modal';

interface IDate {
    day: number;
    date: number;
    month: number;
    year: number;
}

interface Props {
    onDate?: (date: Date) => void;
}

const DatePicker = (props: Props) => {
    const { refs, floatingStyles } = useFloating({
        middleware: [offset(8), flip(), shift({ padding: 8 })],
    });

    const [date, setDate] = useState(new Date());
    const [modalShown, setModalShown] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [daysOfMonth, setDaysOfMonth] = useState<IDate[]>([]);

    useEffect(() => {
        getDaysOfCurrentMonth();
    }, [currentMonth]);

    useEffect(() => {
        props.onDate?.(date);
    }, [date]);

    const getDaysOfCurrentMonth = () => {
        const date = new Date();
        date.setDate(1); // Set it to the first day of the month
        date.setMonth(currentMonth.getMonth());
        date.setFullYear(currentMonth.getFullYear());
        date.setHours(0, 0, 0, 0); // Set date to midnight: timestamp 00:00:00

        const listOfDays = [];
        while (date.getMonth() === currentMonth.getMonth()) {
            listOfDays.push({
                day: date.getDay(),
                date: date.getDate(),
                month: date.getMonth(),
                year: date.getFullYear(),
            });

            date.setDate(date.getDate() + 1);
        }
        setDaysOfMonth(listOfDays);
    };

    const updateCurrentMonth = (leap: number) => {
        const newMonth = currentMonth.setMonth(currentMonth.getMonth() + leap);
        setCurrentMonth(new Date(newMonth));
    };

    const onDateSelect = (date: IDate) => {
        const selectedDate = new Date(date.year, date.month, date.date);
        setModalShown(false);
        setDate(selectedDate);
    };

    const toggleShowModal = useCallback(
        (ev: MouseEvent) => {
            ev.preventDefault();
            setModalShown(!modalShown);
        },
        [modalShown]
    );

    return (
        <div className={styles.container}>
            <button
                ref={refs.setReference}
                onClick={toggleShowModal}
                className={styles.mainButton}
                onBlur={() => setModalShown(false)}
            >
                <DateIcon size={16} color={'#414552'} />
                <span className={styles.formattedDate}>
                    {formatDate(date, 'dd MMMM, yyyy', { locale: es })}
                </span>
            </button>
            {modalShown && (
                <DatePickerModal
                    ref={refs.setFloating}
                    styles={floatingStyles}
                    days={daysOfMonth}
                    onDateSelect={onDateSelect}
                    prevMonth={() => updateCurrentMonth(-1)}
                    nextMonth={() => updateCurrentMonth(1)}
                />
            )}
        </div>
    );
};

export default DatePicker;
